/**
 * @file VariantObjFieldContainer.js
 * @description
 *
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 *
 * @author don michael
 * @since Mar 24, 2022 13:48
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import genericChangeHandler from 'src/@common/genericChangeHandler';
import VariantObjField from './VariantObjField';
import { objectDeepCopy, objectExists, stringExists } from 'src/@common/utilities';

function VariantObjFieldContainer(props) {
    const {
        annotationType,
        fieldSchema,
        biocurations,
        variantObj,
        isArrayElement,
        handleBlur,
        handleDelete,
        handleGDotLookup,
    } = props;

    // start: redux state --------------------------
    // end: redux state -----------------------------

    const [formFields, setFormFields] = useState({});
    const [formIsDirty, setFormIsDirty] = useState(false);
    const [fieldDirty, setFieldDirty] = useState({});
    const [lookupGDot, setLookupGDot] = useState(false);

    useEffect(() => {
        // initalize the local form fields
        let localObj = {};

        if (objectExists(variantObj)) {
            localObj = objectDeepCopy(variantObj);
            setFormFields(localObj);
        }
    }, [variantObj]);

    useEffect(() => {

        if (objectExists(formFields)) {

            // lookup core fields must exist
            if (stringExists(formFields.cDot) &&
                stringExists(formFields.genomeBuild) &&
                stringExists(formFields.transcript)) {

                // case 1: hg38.gDot does not exist
                if (objectExists(formFields.gDotObj) &&
                    objectExists(formFields.gDotObj.hg38) &&
                    !stringExists(formFields.gDotObj.hg38.gDot)) {

                    setLookupGDot(true);

                } else if (objectExists(fieldDirty)) {
                    // case 2: cDot, transcript, and/or genomeBuild has been changed
                    if (fieldDirty.cDot || fieldDirty.transcript || fieldDirty.genomeBuild) {
                        setLookupGDot(true);
                    }

                } else {
                    setLookupGDot(false);
                }

            } else {
                setLookupGDot(false);
            }
        }

    }, [formFields, fieldDirty]);

    const fieldUpdater = (fieldName, value) => {

        // updates localState only
        if (fieldName) {

            switch (fieldName) {
                default:
                    setFormFields({
                        ...formFields,
                        [fieldName]: value
                    });
                    setFormIsDirty(true);
                    setFieldDirty({
                        ...fieldDirty,
                        [fieldName]: true,
                    });
                    break;
            }

        }
    };

    const handleChange = (event, fieldName) => {
        genericChangeHandler(event, fieldName, fieldUpdater);
    };

    const handleLookup = () => {
        handleGDotLookup(
            formFields._id,
            formFields.cDot,
            formFields.transcript);
    };

    const localHandleBlur = (fieldName, value) => {

        let tmpFormFields = null;

        switch (fieldName) {
            case 'gDotObj':
                tmpFormFields = objectDeepCopy(formFields);
                tmpFormFields.gDotObj = objectDeepCopy(value);
                setFormFields(tmpFormFields);

                // update parent state
                handleBlur(tmpFormFields);
                setFormIsDirty(false);
                break;

            case 'pDotObj':
                tmpFormFields = objectDeepCopy(formFields);
                tmpFormFields.pDotObj = objectDeepCopy(value);
                setFormFields(tmpFormFields);

                // update parent state
                handleBlur(tmpFormFields);
                setFormIsDirty(false);
                break;

            default:
                if (formIsDirty === true) {
                    // update parent state
                    handleBlur(formFields);
                    setFormIsDirty(false);
                }
                break;
        }

    };

    return <VariantObjField
        annotationType={annotationType}
        biocurations={biocurations}
        fieldSchema={fieldSchema}
        formFields={formFields}
        isArrayElement={isArrayElement}
        lookupGDot={lookupGDot}
        handleBlur={localHandleBlur}
        handleChange={handleChange}
        handleDelete={handleDelete}
        handleLookup={handleLookup}
    />
}

VariantObjFieldContainer.propTypes = {
    annotationType: PropTypes.string.isRequired,
    fieldSchema: PropTypes.array.isRequired,
    biocurations: PropTypes.object.isRequired,
    variantObj: PropTypes.object.isRequired,
    isArrayElement: PropTypes.bool,
    handleBlur: PropTypes.func.isRequired,
    handleDelete: PropTypes.func.isRequired,
    handleGDotLookup: PropTypes.func.isRequired,
};

export default VariantObjFieldContainer;